/**
 * Pure helpers for report attachments: which field holds them per report
 * type, how the SharePoint sync response is merged back, and how server-side
 * truth (`sharepoint_id`, failure flags) is preserved across saves.
 */
import type { TReportType } from "../../types/reporting.types";

/** Subfolder of the report folder that holds report-level uploads. */
export const ADDITIONAL_DOCUMENTS_FOLDER = "Additional documents";

export type ReportFileUploadError = {
	code?: string;
	userMessage?: string;
};

export type ReportFileEntry = {
	uid: string;
	file_name?: string;
	actual_name?: string;
	name?: string;
	sharepoint_id?: string;
	owner_email?: string;
	/** Set when the last sync could not store the file in SharePoint. */
	upload_status?: "failed";
	upload_error?: ReportFileUploadError;
	[key: string]: unknown;
};

export type PostSaveOutcome = {
	added_files?: { uid: string; sharepoint_id?: string }[];
	failed_uploads?: {
		uid?: string;
		file_name?: string;
		error?: { code?: string; message?: string; userMessage?: string };
	}[];
};

export type NestedListConfig = {
	listName: "findings" | "follow_up_actions";
	parentFolderName: string;
	idField: "finding_number" | "action_number";
};

export type FailedAttachment = {
	uid: string;
	fileName: string;
	/** Where the file sits on the report, e.g. "Additional documents" or "#Action 1". */
	location: string;
	userMessage: string;
};

const TOP_LEVEL_FILE_FIELDS = ["file_upload", "files"] as const;
const NESTED_LIST_FIELDS = ["follow_up_actions", "findings"] as const;
const FILE_TRUTH_KEYS = [
	"sharepoint_id",
	"owner_email",
	"upload_status",
	"upload_error",
] as const;

const DEFAULT_FAILURE_MESSAGE =
	"This file could not be stored in SharePoint. Save the report again to retry.";

/** REST path segment of a report type's API. */
export const getReportApiPath = (reportType: TReportType): string => {
	switch (reportType) {
		case "audit":
			return "audits";
		case "non_conformance":
			return "non-conformance";
		case "lesson_learned":
			return "lesson-learned";
		default:
			return reportType;
	}
};

/** `type` expected by the SharePoint save routes (historical spelling). */
export const getReportSharepointType = (reportType: TReportType): string =>
	reportType === "audit" ? "audits" : reportType;

/** Field that holds report-level uploads. */
export const getReportFileField = (
	reportType: TReportType
): "files" | "file_upload" =>
	reportType === "audit" ? "files" : "file_upload";

/**
 * Subfolder that report-level uploads go into. Lesson learned reports have
 * always stored them directly in the report folder; moving them would split
 * existing attachments across two folders.
 */
export const getReportUploadFolderName = (reportType: TReportType): string =>
	reportType === "lesson_learned" ? "" : ADDITIONAL_DOCUMENTS_FOLDER;

/**
 * Baseline to diff against when deciding which SharePoint items to delete.
 * A persisted list that is empty while the last synced copy still had entries
 * is far more likely a form-state glitch (field hidden, reset or stale) than a
 * deliberate bulk removal, so no removals are derived from it: an orphaned
 * item in SharePoint is recoverable, a deleted one is not.
 */
export const pickRemovalBaseline = <T>(
	current: T[] | undefined,
	baseline: T[] | undefined
): { baseline: T[]; suppressed: boolean } => {
	const previous = Array.isArray(baseline) ? baseline : [];
	if (!Array.isArray(current)) {
		return { baseline: [], suppressed: previous.length > 0 };
	}
	if (current.length === 0 && previous.length > 0) {
		return { baseline: [], suppressed: true };
	}
	return { baseline: previous, suppressed: false };
};

/** Nested list whose items carry their own `documents`. */
export const getNestedListConfig = (
	reportType: TReportType
): NestedListConfig =>
	reportType === "audit"
		? {
				listName: "findings",
				parentFolderName: "Findings",
				idField: "finding_number",
			}
		: {
				listName: "follow_up_actions",
				parentFolderName: "Follow up actions",
				idField: "action_number",
			};

export const getReportFileDisplayName = (file: ReportFileEntry): string =>
	file.actual_name || file.name || file.file_name || file.uid;

/**
 * The name a document attached to a report item, such as a finding, is listed
 * under. Unlike a report-level file it puts `name` first, as the drawer always
 * has, so the page and the drawer show the same name.
 */
export const getReportDocumentName = (
	doc: Partial<ReportFileEntry> | undefined
): string => doc?.name || doc?.actual_name || doc?.file_name || "Document"; // NOSONAR: an empty stored name must fall back too

/** How a report document is shown in the preview: "none" offers Download. */
export type ReportDocumentPreviewKind = "pdf" | "image" | "office" | "none";

// The images a preview shows, by file extension. It shows them from a
// same-origin blob URL, so only these raster types: anything else, SVG
// included, is never previewed.
const PREVIEW_IMAGE_TYPES = new Map([
	["png", "image/png"],
	["jpg", "image/jpeg"],
	["jpeg", "image/jpeg"],
	["gif", "image/gif"],
	["webp", "image/webp"],
	["bmp", "image/bmp"],
]);

// Word, Excel and PowerPoint files, which SharePoint's viewer shows.
const OFFICE_EXTENSIONS = new Set([
	"doc",
	"docx",
	"docm",
	"xls",
	"xlsx",
	"xlsm",
	"ppt",
	"pptx",
	"pptm",
]);

const getReportDocumentExtension = (
	doc: Partial<ReportFileEntry> | undefined
): string => {
	const name = getReportDocumentName(doc);
	const dot = name.lastIndexOf(".");
	return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
};

/** The image type a document previews as, if it is one of the raster types. */
export const getReportDocumentImageType = (
	doc: Partial<ReportFileEntry> | undefined
): string | undefined =>
	PREVIEW_IMAGE_TYPES.get(getReportDocumentExtension(doc));

export const getReportDocumentPreviewKind = (
	doc: Partial<ReportFileEntry> | undefined
): ReportDocumentPreviewKind => {
	const extension = getReportDocumentExtension(doc);
	if (extension === "pdf") return "pdf";
	if (PREVIEW_IMAGE_TYPES.has(extension)) return "image";
	if (OFFICE_EXTENSIONS.has(extension)) return "office";
	return "none";
};

/**
 * The documents a report page lists from a stored list, such as an item's
 * documents or a lesson's attachments. One still uploading, or whose upload
 * failed while the form was open, has no file behind it and is left out. One
 * the save could not store in SharePoint stays listed, flagged, as the drawer
 * flags it: it may still open from its staged copy.
 */
export const getShownReportDocuments = (list: unknown): any[] =>
	(Array.isArray(list) ? list : []).filter(
		(doc: any) => doc && doc.status !== "uploading" && doc.status !== "error"
	);

const isEntryArray = (value: unknown): value is ReportFileEntry[] =>
	Array.isArray(value);

/**
 * Merge a sync response into the current entries by uid: uploaded files gain
 * their `sharepoint_id` and lose any failure flag, failed uploads are flagged
 * for the user. Entries are never removed here.
 */
export const applyPostSaveResult = (
	currentFiles: ReportFileEntry[],
	result: PostSaveOutcome | null | undefined,
	ownerEmail?: string
): ReportFileEntry[] => {
	const added = new Map(
		(result?.added_files ?? []).map((file) => [file.uid, file])
	);
	const failed = new Map(
		(result?.failed_uploads ?? [])
			.filter((file) => file.uid)
			.map((file) => [file.uid, file])
	);

	return currentFiles.map((file) => {
		const uploaded = added.get(file.uid);
		if (uploaded?.sharepoint_id) {
			const { upload_status: _status, upload_error: _error, ...rest } = file;
			return {
				...rest,
				sharepoint_id: uploaded.sharepoint_id,
				owner_email: ownerEmail || file.owner_email || "-",
			};
		}

		const failure = failed.get(file.uid);
		if (failure) {
			return {
				...file,
				upload_status: "failed",
				upload_error: {
					code: failure.error?.code,
					userMessage: failure.error?.userMessage || failure.error?.message,
				},
			};
		}

		return file;
	});
};

const collectServerEntries = (
	serverReport: Record<string, any> | null | undefined
): Map<string, ReportFileEntry> => {
	const byUid = new Map<string, ReportFileEntry>();
	if (!serverReport) return byUid;

	const remember = (entries: unknown) => {
		if (!isEntryArray(entries)) return;
		for (const entry of entries) {
			if (entry?.uid) byUid.set(entry.uid, entry);
		}
	};

	for (const field of TOP_LEVEL_FILE_FIELDS) remember(serverReport[field]);
	for (const listName of NESTED_LIST_FIELDS) {
		const items = serverReport[listName];
		if (!Array.isArray(items)) continue;
		for (const item of items) remember(item?.documents);
	}
	return byUid;
};

const overlayEntries = (
	entries: ReportFileEntry[],
	serverEntries: Map<string, ReportFileEntry>
): ReportFileEntry[] =>
	entries.map((entry) => {
		const server = entry?.uid ? serverEntries.get(entry.uid) : undefined;
		if (!server) return entry;
		const merged: Record<string, unknown> = { ...entry };
		for (const key of FILE_TRUTH_KEYS) {
			if (merged[key] == null && server[key] != null) {
				merged[key] = server[key];
			}
		}
		return merged as ReportFileEntry;
	});

/**
 * Copy `sharepoint_id`, `owner_email` and failure flags from the server copy
 * onto outgoing entries that lack them, so a save that started from stale form
 * state cannot regress an attachment to "never uploaded". Idempotent.
 */
export const overlayServerFileTruth = <T extends Record<string, any>>(
	payload: T,
	serverReport: Record<string, any> | null | undefined
): T => {
	if (!payload || typeof payload !== "object") return payload;
	const serverEntries = collectServerEntries(serverReport);
	if (serverEntries.size === 0) return payload;

	const next: Record<string, any> = { ...payload };
	for (const field of TOP_LEVEL_FILE_FIELDS) {
		if (isEntryArray(next[field])) {
			next[field] = overlayEntries(next[field], serverEntries);
		}
	}
	for (const listName of NESTED_LIST_FIELDS) {
		if (!Array.isArray(next[listName])) continue;
		next[listName] = next[listName].map((item: any) =>
			isEntryArray(item?.documents)
				? { ...item, documents: overlayEntries(item.documents, serverEntries) }
				: item
		);
	}
	return next as T;
};

/** Flagged attachments on a report, top-level and nested, for display. */
export const collectFailedAttachments = (
	report: Record<string, any> | null | undefined
): FailedAttachment[] => {
	if (!report) return [];
	const failed: FailedAttachment[] = [];

	const collect = (entries: unknown, location: string) => {
		if (!isEntryArray(entries)) return;
		for (const entry of entries) {
			if (entry?.upload_status !== "failed") continue;
			failed.push({
				uid: entry.uid,
				fileName: getReportFileDisplayName(entry),
				location,
				userMessage: entry.upload_error?.userMessage || DEFAULT_FAILURE_MESSAGE,
			});
		}
	};

	for (const field of TOP_LEVEL_FILE_FIELDS) {
		collect(report[field], ADDITIONAL_DOCUMENTS_FOLDER);
	}
	for (const listName of NESTED_LIST_FIELDS) {
		const items = report[listName];
		if (!Array.isArray(items)) continue;
		for (const item of items) {
			collect(
				item?.documents,
				item?.action_number || item?.finding_number || "Documents"
			);
		}
	}
	return failed;
};
